import { useState } from 'react';
import { Languages, MessageCircle, Highlighter, Bookmark, Check, RotateCcw } from 'lucide-react';
import { IconButton, Toast } from '@hump/ui';
import { useTranslateStore } from '../../stores/translate';
import { useAnnotationStore } from '../../stores/annotation';
import { useReaderStore } from '../../stores/reader';
import { knowledgeRepository } from '../../lib/repository/knowledge';
import { isConfigured } from '../../lib/ai/config';

interface BlockActionsProps {
  docId: string;
  blockId: string;
  original: string;
  transStatus: string;
  onAskAi: (blockId: string) => void;
}

export function BlockActions({ docId, blockId, original, transStatus, onAskAi }: BlockActionsProps) {
  const translateBlock = useTranslateStore((s) => s.translateBlock);
  const translating = useTranslateStore((s) => s.translating);
  const openEditor = useAnnotationStore((s) => s.openEditor);
  const docTitle = useReaderStore((s) => s.docIndex?.meta.title);
  const configured = isConfigured();
  const [saved, setSaved] = useState(false);

  async function handleSaveSnippet() {
    try {
      await knowledgeRepository.addSnippet(original, docId, blockId, docTitle);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('[Hump] Save snippet failed:', e);
    }
  }

  const isDone = transStatus === 'done';
  const translateTitle = !configured ? '请先配置 AI' : isDone ? '重译此段' : '翻译此段';

  return (
    <div className="flex items-center gap-1 mt-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      {/* 翻译/重译 — 移动端和桌面端都显示 */}
      <IconButton size="sm" onClick={() => translateBlock(docId, blockId)} disabled={!configured || translating}
        title={translateTitle} aria-label={isDone ? '重译' : '翻译'}>
        {isDone ? <RotateCcw className="w-4 h-4" /> : <Languages className="w-4 h-4" />}
      </IconButton>

      {/* 收藏段落 */}
      <IconButton size="sm" onClick={handleSaveSnippet} title="收藏段落" aria-label="收藏段落">
        {saved ? <Check className="w-4 h-4 text-oasis" /> : <Bookmark className="w-4 h-4" />}
      </IconButton>

      {/* 问 AI */}
      <IconButton size="sm" onClick={() => onAskAi(blockId)} disabled={!configured}
        title={configured ? '问 AI' : '请先配置 AI'} aria-label="问 AI">
        <MessageCircle className="w-4 h-4" />
      </IconButton>

      {/* 批注 */}
      <IconButton size="sm" onClick={() => openEditor(docId, blockId)} title="添加批注" aria-label="添加批注">
        <Highlighter className="w-4 h-4" />
      </IconButton>

      {saved && <Toast variant="success" duration={1500}>段落已收藏到驼峰</Toast>}
    </div>
  );
}
